import { useContext } from 'react'

import { UA } from '@js/common/UA'

import { Form } from '@f/elements/HookForm'
import yup from '@f/elements/HookForm/validator/yup'

import { QuizContext } from '../../context/QuizContext'
import QuestionHeading from '../../partials/QuestionHeading'
import { QuizStep } from '../../partials/types'
import { budgetRanges } from '../../partials/utils'
import { SliderElement } from './components'
import { calculateBudgetRange } from './utils'

type ExpectedValueSchema =
  | {
      building_costs: number
      property_costs: number
    }
  | {
      purchase_price: number
      modernization_costs: number
    }

interface SliderElementProps {
  name: string
  label: string
  min: number
  max: number
  minLabel?: string
  maxLabel?: string
  step: number
  default: number
  valueSuffix: string
  valuePrefix: string
  stepId: string
}

interface StepConfig {
  heading: string
  subheading: string
  firstSlider: SliderElementProps
  secondSlider: SliderElementProps
}

export const QuestionExpectedValue = ({ step }: { step: QuizStep }) => {
  const context = useContext(QuizContext)
  const loanPurpose = context?.data?.steps?.find(
    (s: QuizStep) => s.id === 'loan_purpose'
  )?.values?.[0]?.value

  const userData = UA.getLS()

  const budgetRange = budgetRanges.find(
    range => range.range === userData?.budget_range
  )

  const defaultBuildingCosts = budgetRange
    ? calculateBudgetRange(budgetRange)
    : step.values?.find(v => v.name === 'building_costs')?.value

  const defaultPropertyCosts =
    step.values?.find(v => v.name === 'property_costs')?.value || 150_000
  const defaultPurchasePrice =
    step.values?.find(v => v.name === 'purchase_price')?.value || 350_000
  const defaultModernizationCost =
    step.values?.find(v => v.name === 'modernization_costs')?.value || 0

  const loanPurposeIsBuilding =
    loanPurpose === 'building_project' || loanPurpose === undefined

  const stepConfigForBuildingProject: StepConfig = {
    heading:
      'Welche Annahmen sollen wir Ihrem Finanzierungsangebot zugrunde legen?',
    subheading: '',
    firstSlider: {
      name: 'building_costs',
      min: 100_000,
      default: defaultBuildingCosts,
      max: 750_000,
      step: 50_000,
      valuePrefix: '',
      valueSuffix: '€',
      label: 'Erwarteter Objektwert Ihres Hauses',
      stepId: step.id
    },
    secondSlider: {
      name: 'property_costs',
      min: 50_000,
      default: defaultPropertyCosts,
      max: 250_000,
      valuePrefix: '',
      valueSuffix: '€',
      step: 25_000,
      stepId: step.id,
      label: 'Erwarteter Objektwert Ihres Grundstücks'
    }
  }
  const stepConfigForPropertyPurchase: StepConfig = {
    heading:
      'Welche Annahmen sollen wir Ihrem Finanzierungsangebot zugrunde legen?',
    subheading: '',
    firstSlider: {
      name: 'purchase_price',
      min: 100_000,
      default: defaultPurchasePrice,
      max: 750_000,
      step: 50_000,
      stepId: step.id,
      valuePrefix: '',
      valueSuffix: '€',
      label: 'Erwarteter Kaufpreis Ihrer Immobilie'
    },
    secondSlider: {
      name: 'modernization_costs',
      min: 0,
      minLabel: '0 €',
      default: defaultModernizationCost,
      max: 100_000,
      step: 10_000,
      stepId: step.id,
      valuePrefix: '',
      valueSuffix: '€',
      label: 'Erwarteter Modernisierungskosten'
    }
  }

  const stepConfig = loanPurposeIsBuilding
    ? stepConfigForBuildingProject
    : stepConfigForPropertyPurchase

  const defaultValues = {
    [stepConfig.firstSlider.name]: stepConfig.firstSlider.default,
    [stepConfig.secondSlider.name]: stepConfig.secondSlider.default
  }

  const expectedValueValidationSchema: yup.ObjectSchema<ExpectedValueSchema> = loanPurposeIsBuilding
    ? yup.object({
        building_costs: yup
          .number()
          .required()
          .min(stepConfig.firstSlider.min)
          .max(stepConfig.firstSlider.max),
        property_costs: yup
          .number()
          .required()
          .min(stepConfig.secondSlider.min)
          .max(stepConfig.secondSlider.max)
      })
    : yup.object({
        purchase_price: yup
          .number()
          .required()
          .min(stepConfig.firstSlider.min)
          .max(stepConfig.firstSlider.max),
        modernization_costs: yup
          .number()
          .required()
          .min(stepConfig.secondSlider.min)
          .max(stepConfig.secondSlider.max)
      })

  return (
    <>
      <QuestionHeading heading={step.heading} subheading={''} />
      <div className='[ f-wrap ]'>
        <Form.Wrapper
          onSubmit={() => {
            // Submission happens in SliderElement
          }}
          defaultValues={defaultValues}
          validationSchema={expectedValueValidationSchema}>
          <SliderElement
            stepId={step.id}
            name={stepConfig.firstSlider.name}
            label={stepConfig.firstSlider.label}
            minValue={stepConfig.firstSlider.min}
            maxValue={stepConfig.firstSlider.max}
            valueStep={stepConfig.firstSlider.step}
            valueSuffix='€'
            valuePrefix=''
          />
          <SliderElement
            stepId={step.id}
            name={stepConfig.secondSlider.name}
            label={stepConfig.secondSlider.label}
            minValue={stepConfig.secondSlider.min}
            maxValue={stepConfig.secondSlider.max}
            minLabel={stepConfig.secondSlider.minLabel}
            valueStep={stepConfig.secondSlider.step}
            valueSuffix='€'
            valuePrefix=''
          />
        </Form.Wrapper>
      </div>
    </>
  )
}
